import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { buildAbility, type Action, type AppAbility } from '@/lib/ability';
import { permissionsAtom, type RoleName } from '@/stores/permissions.store';
import { useAuth } from '@/hooks/use-auth';

const RBAC_ENABLED = import.meta.env.VITE_RBAC_ENABLED === 'true';

export interface PermissionsApi {
	ability: AppAbility;
	can: (action: Action, subject: string) => boolean;
	role: RoleName | null;
	isSuperAdmin: boolean;
	isLoading: boolean;
	currentUserId: string | null;
	rbacEnabled: boolean;
}

export function usePermissions(): PermissionsApi {
	const perms = useAtomValue(permissionsAtom);
	const { currentUser } = useAuth();

	const ability = useMemo(() => {
		if (!RBAC_ENABLED) return buildAbility([], true);
		if (!perms) return buildAbility([], false);
		return buildAbility(perms.permissions, perms.isSuperAdmin);
	}, [perms]);

	return {
		ability,
		can: (action, subject) => ability.can(action, subject),
		role: perms?.role ?? null,
		isSuperAdmin: !!perms?.isSuperAdmin || !RBAC_ENABLED,
		isLoading: RBAC_ENABLED && !perms,
		currentUserId: currentUser?.id ?? null,
		rbacEnabled: RBAC_ENABLED,
	};
}
